<template>
  <div class="d-flex flex-column h-100 right-container">
    <RightBarHeader v-if="incident" :incident="incident" />
    <div class="d-flex flex-grow-1 inner-right-container">
      <div v-if="incident" class="text-center list-items overflow-y-auto">
        <v-list class="py-0">
          <div
            v-for="item in items"
            :key="item.value"
            class="item_right"
            :class="{ right_tab_item_active: active_el == item.value }"
            @click="handleComponents(item.value)">
            <v-icon size="large">
              {{ item.icon }}
            </v-icon>
            <p class="mt-1 items_list_text">{{ t(item.title) }}</p>
            <v-tooltip activator="parent" location="start">{{ t(item.title) }}</v-tooltip>
          </div>
        </v-list>
      </div>
      <div v-if="loadingIncident" class="d-flex justify-center align-center flex-grow-1">
        <v-progress-circular indeterminate></v-progress-circular>
      </div>
      <div v-else-if="statusCode != undefined" class="d-flex flex-grow-1 justify-center align-center">
        <v-alert
          v-if="statusCode === 404"
          class="ma-4"
          :text="$t('messages.error.incidentNotFound')"
          type="error"
          rounded
          max-width="480px"></v-alert>
        <v-alert
          v-else-if="statusCode === 403"
          class="ma-4"
          :text="$t('messages.error.incidentNoAccess')"
          type="warning"
          rounded
          max-width="480px"></v-alert>
        <v-alert v-else class="ma-4" :text="$t('messages.error.generic')" type="warning" rounded max-width="480px"></v-alert>
      </div>
      <div v-else class="d-flex flex-column flex-grow-1">
        <RightBarDetails
          v-if="detailsDisplay && !editDetails && incident"
          :incident="incident"
          @open-edit-details-event="openEditDetails" />
        <EditDetails v-if="detailsDisplay && editDetails && incident" v-model="incident" @close-edit-details-event="closeEditDetails" />

        <template v-if="fuelDisplay">
          <RightBarContainerTabs v-model="bufferTab" :tabs="buffers"></RightBarContainerTabs>
          <v-window v-model="bufferTab" class="window-scrollable">
            <v-window-item v-for="{ value } in buffers" :key="value" :value="value">
              <RightBarFuel :buffer="value" />
            </v-window-item>
          </v-window>
        </template>
        <template v-if="topoDisplay">
          <RightBarContainerTabs v-model="bufferTab" :tabs="buffers"></RightBarContainerTabs>
          <v-window v-model="bufferTab" class="window-scrollable">
            <v-window-item v-for="{ value } in buffers" :key="value" :value="value">
              <RightBarTopo :buffer="value" />
            </v-window-item>
          </v-window>
        </template>
        <template v-if="weatherDisplay">
          <RightBarContainerTabs v-model="bufferTab" :tabs="buffers"></RightBarContainerTabs>
          <v-window v-model="bufferTab" class="window-scrollable">
            <v-window-item v-for="{ value } in buffers" :key="value" :value="value">
              <RightBarWeather :buffer="value" />
            </v-window-item>
          </v-window>
        </template>

        <RightBarRisk v-if="riskDisplay && incident" :incident="incident" />
        <RightBarFobs v-if="fobsDisplay" :incident="incident" @open-new-observation-event="openNewObservation" />
        <RightBaFobsNewObservation v-if="fobsNewObservationDisplay" @close-new-observation-event="closeNewObservation" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Frameworks Import
import { computed, ref, watch } from 'vue'

import Color from '@arcgis/core/Color.js'
import { useI18n } from 'vue-i18n'

import useLayers from '@tsyl/karonte/lib/composables/useLayers'
import type { GeojsonLayerRaw } from '@tsyl/karonte/lib/types/layers'
import { LayerBuilder } from '@tsyl/karonte/lib/utils/LayerBuilder'
const { removeLayerById, allLayers } = useLayers()

// Types import
import { useQueryNavigation } from '@tsyl/vueabuse'

import RightBarWeather from '@/components/RightBarComponent/Weather/RightBarWeather.vue'
import AuthStore from '@/stores/auth.ts'
import IncidentStore from '@/stores/incidentStore'
import type { Incident, Polygon } from '@/types/Incident'
import type Item from '@/types/Item'

// Component Import
import type { BufferTab } from '@/types/RightContainer'

import EditDetails from './Details/EditDetails.vue'
import RightBarDetails from './Details/RightBarDetails.vue'
import RightBarFobs from './Fobs/RightBarFobs.vue'
import RightBaFobsNewObservation from './Fobs/RightBarFobsNewObservation.vue'
import RightBarContainerTabs from './RightBarContainerTabs.vue'
import RightBarFuel from './RightBarFuel.vue'
import RightBarHeader from './RightBarHeader.vue'
import RightBarRisk from './Risk/RightBarRisk.vue'
import RightBarTopo from './Topo/RightBarTopo.vue'

import { storeToRefs } from 'pinia'

// Stores
const incidentStore = IncidentStore()
const { waitUntilInitialised } = AuthStore()
const { incident, loadingIncident, statusCode } = storeToRefs(incidentStore)

const { queryModelValue } = useQueryNavigation()
const queryIncidentValue = queryModelValue('incident')

const { t } = useI18n()

const bufferTab = ref('1')

const active_el = ref('info' as string)

const items = [
  {
    icon: 'mdi-information',
    title: 'views.incidentPanel.tabs.details',
    value: 'info'
  },
  {
    icon: 'mdi-weather-sunny',
    title: 'views.incidentPanel.tabs.weather',
    value: 'weather'
  },
  {
    icon: 'mdi-pine-tree',
    title: 'views.incidentPanel.tabs.fuels',
    value: 'fuels'
  },
  {
    icon: 'mdi-image-filter-hdr',
    title: 'views.incidentPanel.tabs.topo',
    value: 'topo'
  },
  {
    icon: 'mdi-shield-home',
    title: 'views.incidentPanel.tabs.risks',
    value: 'risk'
  },
  {
    icon: 'mdi-form-select',
    title: 'views.incidentPanel.tabs.fobs',
    value: 'fobs'
  }
] as Array<Item>

const buffers = computed<BufferTab[]>(() => ['1', '5', '10'].map((value) => ({ value, text: `${t('domain.fields.buffer')} ${value}` })))
const detailsDisplay = ref(true as boolean)
const weatherDisplay = ref(false as boolean)
const fuelDisplay = ref(false as boolean)
const topoDisplay = ref(false as boolean)
const riskDisplay = ref(false as boolean)
const fobsDisplay = ref(false as boolean)
const fobsNewObservationDisplay = ref(false as boolean)
const editDetails = ref(false as boolean)

const handleComponents = (value: string) => {
  detailsDisplay.value = false
  weatherDisplay.value = false
  fuelDisplay.value = false
  topoDisplay.value = false
  riskDisplay.value = false
  fobsDisplay.value = false
  fobsNewObservationDisplay.value = false

  active_el.value = value

  switch (value) {
    case 'info':
      detailsDisplay.value = true
      break
    case 'weather':
      weatherDisplay.value = true
      break
    case 'fuels':
      fuelDisplay.value = true
      break
    case 'topo':
      topoDisplay.value = true
      break
    case 'risk':
      riskDisplay.value = true
      break
    case 'fobs':
      fobsDisplay.value = true
      break
  }
}

const openEditDetails = () => {
  editDetails.value = true
}

const closeEditDetails = () => {
  editDetails.value = false
}

const openNewObservation = () => {
  fobsDisplay.value = false
  fobsNewObservationDisplay.value = true
}

const closeNewObservation = () => {
  fobsDisplay.value = true
  fobsNewObservationDisplay.value = false
}

function buildBufferLayer(incident: Incident) {
  const layer: { type: string; features: Polygon[] } = {
    type: 'FeatureCollection',
    features: [incident.buffer_1, incident.buffer_5, incident.buffer_10, incident.perimeter].filter((x) => x !== undefined) as Polygon[]
  }
  return JSON.stringify(layer)
}

watch(
  () => queryIncidentValue.value,
  async (val) => {
    if (val) {
      waitUntilInitialised().then(async () => {
        await incidentStore.fetchIncident(val)
        await loadKaronteBufferLayer(incident.value)
      })
    }
  },
  {
    immediate: true
  }
)

async function loadKaronteBufferLayer(incident: Incident | undefined) {
  try {
    if (incident === undefined) return -1
    removeLayerById('incident_buffer_layer')
    const bufferLayer: GeojsonLayerRaw = {
      id: 'incident_buffer_layer',
      data: () => {
        return buildBufferLayer(incident)
      },
      title: {
        'en-US': 'Incident Buffer Layer',
        'pt-BR': 'Camada de buffer de incidentes',
        'es-ES': 'Capa de Buffer de Incidentes',
        'fr-FR': "Couche tampon d'incident"
      },
      type: 'geojson',
      opacity: 0.35,
      enabled: true,
      customProperties: { group: 'buffers' },
      zindex: 2,
      extraArcgisParameter: {
        orderBy: [{ field: 'buffer', order: 'ascending' }]
      }
    }
    bufferLayer.renderer = {
      type: 'unique-value',
      field: 'buffer',
      defaultSymbol: { type: 'simple-fill' },
      uniqueValueInfos: [
        {
          value: 10,
          symbol: {
            type: 'simple-fill',
            color: new Color('#d7c261')
          }
        },
        {
          value: 5,
          symbol: {
            type: 'simple-fill',
            color: new Color('#d79c61')
          }
        },
        {
          value: 1,
          symbol: {
            type: 'simple-fill',
            color: new Color('#d77661')
          }
        },
        {
          value: 0,
          symbol: {
            type: 'simple-fill',
            color: new Color('#6699ff')
          }
        }
      ]
    }
    const buildedLayer = LayerBuilder(bufferLayer)
    allLayers.value?.push(buildedLayer)
  } catch (error) {
    console.error('Failed to load the Buffer Layer', error)
  }
}
</script>

<style scoped></style>

<style scoped lang="scss">
.right-container {
  background: rgb(var(--v-theme-surface-light)) !important;
}

.right_tab_item_active {
  background-color: rgb(var(--v-theme-surface)) !important;
  border-radius: 5px;
  color: rgb(var(--v-theme-primary));
}

.list-items {
  border-right: 1px solid rgb(var(--v-theme-surface-light)) !important;
  flex: 0 0 auto;
  background-color: rgb(var(--v-theme-surface-light));
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: margin 0.3s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  margin-right: calc(var(--right-panel-width) * -1);
}

.items_list_text {
  font-size: 10px;
}

.item_right {
  border-radius: 0 !important;
  padding: 12px 4px;
  background-color: rgb(var(--v-theme-surface-light));
  width: 70px !important;
}

.item_right:hover {
  cursor: pointer;
  background-color: var(--soft-selected);
  color: rgb(var(--v-theme-primary));
}

.inner-right-container {
  min-height: 0;
  background-color: rgb(var(--v-theme-surface));
}

.window-scrollable {
  flex: 1 1 auto;
  overflow-y: auto;
}
</style>
